import { useContext, useEffect, useState } from "react";
import { Button, Input, Modal, ModalBody, ModalHeader } from "reactstrap";
import { FileUploader } from "react-drag-drop-files";
import parse from "html-react-parser";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import "../styles/SiteDataSheet.css";

import { ModalContext, RestContext } from "../App";
import { DOC_URL, SITE_URL } from "../serverConfig";

function SiteDataSheet({ site }) {
	const [rows, setRows] = useState([]);
	const [editorRows, setEditorRows] = useState([]);
	const [images, setImages] = useState([]);
	const [editMode, setEditMode] = useState(false);
	const [changed, setChanged] = useState(false);

	const { sendGetRequest, sendPostRequest } = useContext(RestContext);
	const { openModal, setOpenModal } = useContext(ModalContext);

	const toggleEditMode = () => setEditMode(!editMode);
	const toggleUploadModal = () =>
		openModal === "data-sheet-upload-modal"
			? setOpenModal()
			: setOpenModal("data-sheet-upload-modal");

	useEffect(() => {
		if (site?.idNo !== "all") {
			sendGetRequest(SITE_URL + "/sheet/" + site.idNo, (response) => {
				setRows(response.data.rows);
				setImages(response.data.images);
			});
		} else {
			setRows([]);
			setImages([]);
		}
	}, [site]);

	useEffect(() => {
		setEditorRows(structuredClone(rows));
		setChanged(false);
	}, [rows]);

	const getColumnLink = (row) => {
		if (row[1]) {
			return (
				<a
					href={`https://www.google.com/maps/search/${row[1].replace(/\s/, "+").toLowerCase()}/@${site?.latitude},${site?.longitude},8z`}
					target="_blank"
				>
					{row[0]}
				</a>
			);
		} else {
			return row[0];
		}
	};

	function linkify(inputText) {
		if (!inputText) return inputText;
		var replacedText, replacePattern1, replacePattern2, replacePattern3;

		//URLs starting with http://, https://, or ftp://
		replacePattern1 =
			/(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
		replacedText = inputText.replace(
			replacePattern1,
			'<a href="$1" target="_blank">$1</a>',
		);

		//URLs starting with "www." (without // before it, or it'd re-link the ones done above).
		replacePattern2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim;
		replacedText = replacedText.replace(
			replacePattern2,
			'$1<a href="http://$2" target="_blank">$2</a>',
		);

		//Change email addresses to mailto:: links.
		replacePattern3 =
			/(([a-zA-Z0-9\-\_\.])+@[a-zA-Z\_]+?(\.[a-zA-Z]{2,6})+)/gim;
		replacedText = replacedText.replace(
			replacePattern3,
			'<a href="mailto:$1">$1</a>',
		);

		replacedText = replacedText.replace(/\n/g, "<br>");

		return parse(replacedText);
	}

	const getRowValue = (row) => {
		return linkify(row[2]) || <span className="novalue">N/A</span>;
	};

	const mapRows = () => {
		if (editMode) {
			return editorRows.map((row, index) => (
				<tr>
					<td className="data-sheet-column">{getColumnLink(row)}</td>
					<td className="data-sheet-value">
						<div className="data-sheet-value-input-wrapper">
							<Input
								className="data-sheet-value-input"
								type="textarea"
								value={row[2]}
								onChange={(event) => {
									const value = event.target.value;
									const newEditorRows = [...editorRows];
									newEditorRows[index][2] = value.length
										? value
										: null;
									setEditorRows(newEditorRows);
									setChanged(true);
								}}
								rows="5"
								maxLength={2000}
							/>
							<div className="data-sheet-value-counter">
								{(row[2]?.length || 0) + " / 2000"}
							</div>
						</div>
					</td>
				</tr>
			));
		} else {
			return rows.map((row) => (
				<tr>
					<td className="data-sheet-column">{getColumnLink(row)}</td>
					<td className="data-sheet-value">{getRowValue(row)}</td>
				</tr>
			));
		}
	};

	const mapImages = () => {
		return images.map((img) => (
			<img
				src={DOC_URL + "/preview/" + img.docId}
				alt={document.docName}
			/>
		));
	};

	const onUpload = () => {
		sendGetRequest(SITE_URL + "/sheet/" + site.idNo, (response) => {
			setImages(response.data.images);
		});
	};

	const saveChanges = () => {
		if (changed && site?.idNo !== "all") {
			const payload = {
				siteId: site.idNo,
				rows: editorRows,
			};
			sendPostRequest(SITE_URL + "/sheet", payload, (response) => {
				sendGetRequest(SITE_URL + "/sheet/" + site.idNo, (response) => {
					setRows(response.data.rows);
					setImages(response.data.images);
					setEditMode(false);
				});
			});
		} else {
			setEditMode(false);
		}
	};

	const getCoordinateString = () => {
		if (site === undefined) return null;
		const latDirection = site.latitude >= 0 ? "°N" : "°S";
		const lonDirection = site.longitude >= 0 ? "°E" : "°W";
		return `${Math.abs(site.latitude) + latDirection}, ${Math.abs(site.longitude) + lonDirection}`;
	};

	const mapDataToPdfRows = (contacts) => {
		const pdfRows = [];
		const style = { lineWidth: 1, lineColor: 40 };
		const dataRows = [
			["Coordinates", null, getCoordinateString()],
			...rows,
		];
		for (let row of dataRows) {
			let cells = [];
			cells.push({
				content: row[0],
				styles: { cellWidth: 75, ...style },
			});
			cells.push({
				content: row[2] || "N/A",
				styles: style,
			});
			pdfRows.push(cells);
		}

		pdfRows.push([
			{
				content: "Contacts",
				colSpan: 2,
				styles: {
					fontStyle: "bold",
					...style,
				},
			},
		]);

		if (contacts?.length) {
			for (let contact of contacts) {
				let cells = [];
				cells.push({
					content: contact.name,
					styles: { cellWidth: 75, ...style },
				});
				let contactString = "";
				for (let contactType of ["email", "office", "cell"]) {
					if (contact[contactType]) {
						contactString += `${contactType[0].toUpperCase() + contactType.slice(1).toLowerCase()}: ${contact[contactType]}\n`;
					}
				}
				cells.push({ content: contactString, styles: style });
				pdfRows.push(cells);
			}
		}

		return pdfRows;
	};

	const generatePdf = () => {
		sendGetRequest(SITE_URL + "/contacts/" + site.idNo, (response) => {
			const contacts = response.data?.contacts;

			const doc = new jsPDF({
				orientation: "portrait",
				format: "letter",
				unit: "px",
			});

			const tableWidth = 400;
			const pageWidth = doc.internal.pageSize.width;
			const margin = (pageWidth - tableWidth) / 2;

			doc.setFontSize(16);

			doc.text(
				[`${site.aqmetAbbr} Data Sheet`],
				doc.internal.pageSize.width / 2,
				20,
				{
					align: "center",
				},
			);

			autoTable(doc, {
				body: mapDataToPdfRows(contacts),
				bodyStyles: {
					fillColor: false,
					textColor: 40,
					fontSize: 10,
					cellPadding: { vertical: 2, horizontal: 2 },
				},
				alternateRowStyles: {
					fillColor: false,
				},
				tableWidth: tableWidth,
				startY: 30,
				margin: { left: margin, right: margin },
			});

			// doc.setFontSize(11);

			// for (let i = 1; i <= doc.internal.getNumberOfPages(); i++) {
			//     doc.setPage(i);
			//     doc.text(
			//         "ARS Phone: 970-484-7941        * Primary Operator       (AC) Administrative Contact",
			//         doc.internal.pageSize.width / 2,
			//         doc.internal.pageSize.height - 10,
			//         { align: "center" }
			//     );
			//     doc.text(
			//         "Page " + i,
			//         doc.internal.pageSize.width - 25,
			//         doc.internal.pageSize.height - 10,
			//         { align: "center" }
			//     );
			// }

			doc.save(`${site.aqmetAbbr}_Data_Sheet`);
		});
	};

	return (
		<div id="site-details-data-sheet">
			<div id="data-sheet-buttons-wrapper">
				<Button onClick={saveChanges} hidden={!editMode}>
					Save
				</Button>
				<Button onClick={generatePdf} hidden={editMode}>
					Download
				</Button>
				<Button onClick={toggleEditMode} hidden={editMode}>
					Edit
				</Button>
			</div>
			<table id="data-sheet-table">
				<tbody>{mapRows()}</tbody>
			</table>
			<div id="data-sheet-images" hidden={editMode}>
				{mapImages()}
			</div>
			<Button id="data-sheet-upload-button" onClick={toggleUploadModal}>
				Upload Image
			</Button>
			<UploadModal
				siteId={site?.idNo}
				isOpen={openModal === "data-sheet-upload-modal"}
				toggle={toggleUploadModal}
				onUpload={onUpload}
			/>
		</div>
	);
}

function UploadModal({ siteId, isOpen, toggle, onUpload }) {
	const { sendPostRequest } = useContext(RestContext);

	const getDefaultDate = () => {
		const date = new Date();
		return date.toJSON().split("T")[0];
	};

	const handleChange = (files) => {
		let docs = [];
		let docTypes = {};
		let docDates = {};
		for (let file of files) {
			if (!docs.some((doc) => doc.name === file.name)) {
				docs.push(file);
				docTypes[file.name] = "DATA_SHEET_IMG";
				docDates[file.name] = getDefaultDate();
			}
		}

		const fileTypes = Object.keys(docTypes).map(
			(docName) => docName + "=" + docTypes[docName],
		);
		const fileTypeString = fileTypes.join(";");
		const fileDates = Object.keys(docDates).map(
			(docName) => docName + "=" + docDates[docName] + " 06:00:00",
		);
		const fileDateString = fileDates.join(";");
		const formData = new FormData();
		for (let doc of docs) {
			formData.append("files", doc);
		}
		formData.append("fileTypes", fileTypeString);
		formData.append("fileDates", fileDateString);
		sendPostRequest(
			DOC_URL + "/sheet/upload/" + siteId,
			formData,
			(response) => {
				onUpload();
				toggle();
			},
		);
	};

	return (
		<Modal id="data-sheet-upload-modal" isOpen={isOpen} toggle={toggle}>
			<ModalHeader toggle={toggle}>Upload Image</ModalHeader>
			<ModalBody>
				<FileUploader
					handleChange={handleChange}
					classes="file-upload"
					name="file"
					types={["PNG", "JPG", "JPEG", "BMP", "GIF", "HEIC", "HEIF"]}
					multiple
				/>
			</ModalBody>
		</Modal>
	);
}

export default SiteDataSheet;
